import { useState } from "react";

import { dateFormat } from "common/dateFormat";
import { SortOrder } from "common/SortOrder";
import {
  getFileNameWithExtension,
  getFileNameWithoutExtension
} from "common/getFileName";
import { Button } from "components/button/Button";
import FolderTree from "components/folderTree/FolderTree";
import { Icon, SortIcon } from "components/icon/Icon";
import Modal from "components/modal/Modal";
import { Tooltip } from "components/tooltip/Tooltip";
import { formatFileSize } from "libs/file-size-format";
import { DeliveryFile } from "models/DeliveryFile";
import { Input } from "components/input/Input";
import { filterYearEndFolders } from "views/documents/state/documentArchiveOptions";
import {
  SortByCategory,
  SortByComment,
  SortByCreatedBy,
  SortByDate,
  SortByName,
  SortBySize,
  SortDeliveryUploadFile
} from "..";
import OverlayLoader from "components/loader/OverlayLoader";
import { useRootStore } from "store";

type DocumentDeliveryTableWithOptionsProps = {
  title: string;
  files: DeliveryFile[];
  isLoading: boolean;
};

export function DocumentDeliveryTableWithOptions({
  title,
  files,
  isLoading
}: DocumentDeliveryTableWithOptionsProps) {
  const { workingCustomerId } = useRootStore((state) => state.global);
  const { archiveRoot, downloadFile } = useRootStore((state) => state.archive);
  const { moveToArchive, setFileAsHandled } = useRootStore(
    (state) => state.delivery
  );
  const [sortFunction, setSorting] = useState<SortDeliveryUploadFile>(
    () => SortByName
  );
  const [sortOrder, setSortOrder] = useState(SortOrder.Descending);
  const setSortingFn = (selectedSortFn: SortDeliveryUploadFile) => {
    setSortOrder(
      sortFunction === selectedSortFn && sortOrder === SortOrder.Descending
        ? SortOrder.Ascending
        : SortOrder.Descending
    );
    setSorting(() => selectedSortFn);
  };
  const [selectedMoveFolderId, setSelectedMoveFolderId] = useState<string>("");
  const [showMoveFileModal, setShowMoveFileModal] = useState(false);
  const [selectedFile, setSelectedFile] = useState<DeliveryFile | undefined>(
    undefined
  );
  const [renameFileName, setRenameFileName] = useState("");

  const openMoveFileModal = (file: DeliveryFile) => {
    setShowMoveFileModal(true);
    setSelectedFile(file);
    setRenameFileName(getFileNameWithoutExtension(file.name));
  };

  const moveUnhandledItemToArchive = (
    pathToMoveTo: string,
    item: DeliveryFile
  ) => {
    setFileAsHandled(item, workingCustomerId);
    moveToArchive(
      item,
      workingCustomerId,
      pathToMoveTo,
      getFileNameWithExtension(renameFileName, item.name)
    );
  };

  const moveHandledItemToArchive = (
    pathToMoveTo: string,
    item: DeliveryFile
  ) => {
    moveToArchive(
      item,
      workingCustomerId,
      pathToMoveTo,
      getFileNameWithExtension(renameFileName, item.name)
    );
  };

  const selectedMoveFolder = archiveRoot.find(
    (r) => r.id === selectedMoveFolderId
  );

  const canMoveToFolder = selectedMoveFolder && selectedMoveFolder.writable;

  return (
    <>
      {showMoveFileModal && (
        <Modal
          modalTitle="move file"
          onDismiss={() => setShowMoveFileModal(false)}
          header={
            <h1 className="mt-0">
              Flytta fil {selectedFile && selectedFile.name}
            </h1>
          }
          footer={
            <>
              <div className="mt-4 d-flex justify-content-end">
                <Button
                  className="mr-3"
                  disabled={
                    !selectedMoveFolder || !canMoveToFolder || !renameFileName
                  }
                  onClick={() => {
                    setShowMoveFileModal(false);

                    if (!selectedFile || !selectedMoveFolder) {
                      return;
                    }

                    if (!selectedFile.handled) {
                      moveUnhandledItemToArchive(
                        selectedMoveFolder.path,
                        selectedFile
                      );
                    } else {
                      moveHandledItemToArchive(
                        selectedMoveFolder.path,
                        selectedFile
                      );
                    }
                  }}
                >
                  Bekräfta
                </Button>

                <Button
                  variant="outline"
                  color="light"
                  onClick={() => {
                    setShowMoveFileModal(false);
                  }}
                >
                  Avbryt
                </Button>
              </div>
            </>
          }
        >
          <div className="overflow-scroll">
            <label>Välj en mapp att flytta filen till</label>

            <hr />

            <FolderTree
              style={{ maxHeight: "30vh" }}
              selectedFolderId={selectedMoveFolderId}
              folders={archiveRoot.filter((item)=>filterYearEndFolders(item, archiveRoot))}
              className="with-selected"
              classNamesFolder="py-2"
              onFolderClick={(folder) => setSelectedMoveFolderId(folder.id)}
            />
            <hr />

            {!canMoveToFolder && (
              <p className="my-4">
                <b>Välj en annan mapp att flytta till</b>
              </p>
            )}

            <label className="mt-4">Ändra filnamn</label>
            {selectedFile && (
              <Input
                placeholder="Välj ett filnamn"
                value={renameFileName}
                onChange={(e) => {
                  setRenameFileName(e.target.value);
                }}
              />
            )}

            {renameFileName.length === 0 && (
              <p>
                <b>Filnamnet måste ha minst ett tecken</b>
              </p>
            )}
          </div>
        </Modal>
      )}

      <h1>{title}</h1>
      <hr className={"header-border"} />
      <OverlayLoader isLoading={isLoading}>
        <>
          <table>
            <thead>
              <tr>
                <th
                  className="cursor-pointer"
                  onClick={() => setSortingFn(SortByName)}
                >
                  <div className="d-flex">
                    Filnamn
                    <SortIcon
                      className="ml-2"
                      sortOrder={sortFunction === SortByName ? sortOrder : ""}
                    />
                  </div>
                </th>
                <th
                  className="cursor-pointer"
                  onClick={() => setSortingFn(SortByCategory)}
                >
                  <div className="d-flex">
                    Kategori
                    <SortIcon
                      className="ml-2"
                      sortOrder={
                        sortFunction === SortByCategory ? sortOrder : ""
                      }
                    />
                  </div>
                </th>
                <th
                  className="cursor-pointer"
                  onClick={() => setSortingFn(SortByComment)}
                >
                  <div className="d-flex">
                    Kommentar
                    <SortIcon
                      className="ml-2"
                      sortOrder={
                        sortFunction === SortByComment ? sortOrder : ""
                      }
                    />
                  </div>
                </th>
                <th
                  className="cursor-pointer"
                  onClick={() => setSortingFn(SortBySize)}
                >
                  <div className="d-flex">
                    Filstorlek
                    <SortIcon
                      className="ml-2"
                      sortOrder={sortFunction === SortBySize ? sortOrder : ""}
                    />
                  </div>
                </th>
                <th
                  className="cursor-pointer"
                  onClick={() => setSortingFn(SortByDate)}
                >
                  <div className="d-flex">
                    Uppladdat den
                    <SortIcon
                      className="ml-2"
                      sortOrder={sortFunction === SortByDate ? sortOrder : ""}
                    />
                  </div>
                </th>
                <th
                  className="cursor-pointer"
                  onClick={() => setSortingFn(SortByCreatedBy)}
                >
                  <div className="d-flex">
                    Uppladdat av
                    <SortIcon
                      className="ml-2"
                      sortOrder={
                        sortFunction === SortByCreatedBy ? sortOrder : ""
                      }
                    />
                  </div>
                </th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {files
                .sort((a, b) => {
                  return (
                    sortFunction(a, b) *
                    (sortOrder === SortOrder.Descending ? 1 : -1)
                  );
                })
                .map((file) => (
                  <tr key={file.id}>
                    <td className="break-word">{file.name}</td>
                    <td>{file.category}</td>
                    <td>{file.comment}</td>
                    <td>{formatFileSize(parseInt(file.fileSize))}</td>
                    <td>{dateFormat(file.createdAt)}</td>
                    <td>{file.createdByName}</td>
                    <td>
                      <div className="d-flex fg-2">
                        <Tooltip
                          className="mr-3"
                          content={<p className="m-0">Ladda ned filen</p>}
                        >
                          <Icon
                            className="cursor-pointer"
                            name="download"
                            onClick={() => downloadFile(file)}
                          />
                        </Tooltip>

                        {!file.handled && (
                          <Tooltip
                            className="mr-3"
                            content={
                              <p className="m-0">Markera filen som hanterad</p>
                            }
                          >
                            <Icon
                              className="cursor-pointer"
                              name="check"
                              onClick={() =>
                                setFileAsHandled(file, workingCustomerId)
                              }
                            />
                          </Tooltip>
                        )}

                        <Tooltip
                          className="mr-3"
                          content={
                            <p className="m-0">
                              Flytta filen till dokumentarkivet
                            </p>
                          }
                        >
                          <Icon
                            className="cursor-pointer"
                            name="arrow-right"
                            onClick={() => openMoveFileModal(file)}
                          />
                        </Tooltip>
                      </div>
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
          {files.length === 0 && <p>Här var det tomt!</p>}
        </>
      </OverlayLoader>
    </>
  );
}
